<template>
  <div class="asset-drawer-values-holders">
    <div class="asset-drawer__form">
      <UIPanel
        v-model="clearedSearch"
        v-model:storage="searchStorage"
        placeholder="Search holders..."
      >
        <template #default="{ ButtonExport, size }">
          <component
            :is="ButtonExport"
            :disabled="isLoading"
            :size
            @click="handleExport"
          />
        </template>
      </UIPanel>
    </div>
    <UIGridSkeleton
      v-if="isLoading"
      :columns
      :footer
      :sm
      message="Loading holders..."
    />
    <UIGrid
      v-else
      v-model:selected="selected"
      v-model:sort="sort"
      :columns
      :footer
      :items
      :sm
      id-field="account_id"
      :selectable="isSelectable"
      scrollable
      display-menu
      @click:menu="handleClickMenu"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref } from 'vue'
import { sumBy } from 'lodash'

import { AnalyticsField, AnalyticsPosition, Sort } from '@types'

import { ANALYTICS_CAPTIONS } from '@/const'

import { getAssetName, getAssetTicker } from '@/views/Analytics/utils/helpers'

import useGridSort from '@/components/hooks/gridSort'
import useGridFilter from '@/components/hooks/gridFilter'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'

import RepositoryPopup from '@/modules/modals/Repositories/Data/RepositoryPopup.vue'
import { UIPanel } from '@ui/core'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { useHandledGridExport } from '@/components/hooks/gridExport'

type Props = {
  assets: string[]
  static?: boolean
}

const props = defineProps<Props>()

defineExpose({
  refresh: () => {
    fetchHolders()
  },
})

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()

const isLoading = ref(true)
const positions = ref<AnalyticsPosition[]>([])

const selected = defineModel<string[]>('selected')
const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sort = ref<Sort[]>()

const isSelectable = computed(() => positions.value.length > 1 && !props.static)

const assetsBunch = computed(() => assetsBunchStore.getList)

const endOfDate = computed(() => repositoriesStore.getCurrentTimelineEnd)

const sm = '1fr 1fr'
const columns = computed(() => [
  {
    name: AnalyticsField.ACCOUNT_ID,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_ID],
    formatter: getAssetName(
      assetsBunch.value,
      repositoriesStore.getCurrentName,
    ),
    cellClasses: 'ui-grid__cell--span-2',
  },
  {
    name: AnalyticsField.AMOUNT,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.AMOUNT],
    formatter: analyticsStore.amountFormatter,
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    footerValueClasses: 'ui-grid__footer-value--right number-node',
  },
  {
    name: AnalyticsField.ASSET_ID,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_ID],
    formatter: (assetId: string) => getAssetTicker(assetsBunch.value)(assetId),
    tooltip: (field: string, item?: AnalyticsPosition) => {
      const key = field as keyof AnalyticsPosition
      return item?.[key]
        ? getAssetName(assetsBunch.value, '')(item[key].toString())
        : undefined
    },
  },
  {
    name: AnalyticsField.POSITION_CLOSE,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_CLOSE],
    formatter: analyticsStore.valueFormatter,
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    footerValueClasses: 'ui-grid__footer-value--right number-node',
  },
  {
    name: AnalyticsField.POSITION_COST,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_COST],
    formatter: analyticsStore.valueFormatter,
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    footerValueClasses: 'ui-grid__footer-value--right number-node',
  },
])

const footer = computed(() => ({
  [AnalyticsField.ACCOUNT_ID]: 'Total',
  [AnalyticsField.AMOUNT]: analyticsStore.amountFormatter(sumBy(inputItems.value, AnalyticsField.AMOUNT)),
  [AnalyticsField.ASSET_ID]: '',
  [AnalyticsField.POSITION_CLOSE]: analyticsStore.valueFormatter(sumBy(inputItems.value, AnalyticsField.POSITION_CLOSE)),
  [AnalyticsField.POSITION_COST]: analyticsStore.valueFormatter(sumBy(inputItems.value, AnalyticsField.POSITION_COST)),
}))


const inputItems = computed(() => positions.value)
const filteredItems = useGridFilter(
  columns,
  inputItems,
  clearedSearch
)
const items = useGridSort(sort, columns, filteredItems)

const fetchHolders = async () => {
  const { position_view } = await analyticsStore.fetch({
    date: endOfDate.value,
    assets: JSON.stringify(props.assets),
    data: 'position',
  })
  positions.value = position_view
  isLoading.value = false
}

const handleClickMenu = (item: any) => {
  let modalInstance: any
  let key: string
  if (item.account_id === AnalyticsField.REPO_ROOT) {
    const repositoryId = repositoriesStore.getCurrentId
    if (!repositoryId) return
    key = `asset-drawer-values-holders-repository-${repositoryId}`
    modalInstance = modalsStore.init(key, markRaw(RepositoryPopup))
    modalInstance?.open(modalsStore.getZIndex(), {
      repository: repositoriesStore.getRepository,
    })
  } else {
    const instance = assetsBunch.value.get(item.account_id)
    if (!instance) return
    modalInstance = modalsStore.init(instance.id, instance, { allowTray: true })
    modalInstance?.open(modalsStore.getZIndex())
  }
}

const handleExport = async () => {
  let name = 'assets_holders'
  if (props.assets.length === 1) {
    const assetName = getAssetName(assetsBunch.value, '')(props.assets[0])
    name = `${assetName}_holders`
  }
  useHandledGridExport(columns.value, items.value, name, true)
}

onMounted(() => {
  if (!props.assets.length) return
  fetchHolders()
})
</script>

<style>
.asset-drawer-values-holders {
  @apply flex flex-col;
  @apply h-1/2;
  @apply pb-4;
}
</style>
